/**
 * Which shell a person belongs in.
 *
 * 02 §6: three shells, one app — Employee (ESS), Manager (MSS), Admin — route
 * guarded by role. The guard is a usability control, not a security one: every
 * request is independently authorised server-side, so a determined user typing
 * /admin gains nothing but an empty screen and a string of 403s.
 *
 * What it does buy is that people are not shown a product that is not theirs.
 * An employee should not have to look at an org chart editor to find the check-
 * in button.
 */

export const ROLE = {
  superAdmin: 'super_admin',
  hrAdmin: 'hr_admin',
  payrollAdmin: 'payroll_admin',
  hrbp: 'hrbp',
  manager: 'manager',
  employee: 'employee',
  auditor: 'auditor',
  dpo: 'dpo',
} as const;

export type Shell = 'admin' | 'mss' | 'ess';

/** Roles that get the administrative shell. */
const ADMIN_ROLES: string[] = [
  ROLE.superAdmin,
  ROLE.hrAdmin,
  ROLE.payrollAdmin,
  ROLE.auditor,
  ROLE.dpo,
];

/** Roles that get the manager shell. */
const MANAGER_ROLES: string[] = [ROLE.manager, ROLE.hrbp];

export function canUseShell(roles: string[], shell: Shell): boolean {
  switch (shell) {
    case 'admin':
      return roles.some((r) => ADMIN_ROLES.includes(r));
    case 'mss':
      // An administrator can look at the manager shell: HR covering for an
      // absent manager is an ordinary Tuesday, and the server decides what they
      // actually see.
      return roles.some((r) => MANAGER_ROLES.includes(r) || ADMIN_ROLES.includes(r));
    case 'ess':
      // Everyone has a self-service view. Even an administrator has their own
      // leave to book — and the DPDP access right (03 §5) is exercised here, so
      // it cannot be a privilege.
      return roles.length > 0;
  }
}

/**
 * Where to send someone after they sign in.
 *
 * Most specific first: an HR Admin who is also an employee wants the admin
 * shell, because that is the job they logged in to do. Somebody with only an
 * employee role goes to self-service.
 */
export function landingShell(roles: string[]): Shell {
  if (canUseShell(roles, 'admin')) return 'admin';
  if (roles.some((r) => MANAGER_ROLES.includes(r))) return 'mss';
  return 'ess';
}

export function shellPath(shell: Shell): string {
  return `/${shell}`;
}

/** The shells a person may switch between, for the header switcher. */
export function availableShells(roles: string[]): Shell[] {
  return (['admin', 'mss', 'ess'] as Shell[]).filter((s) => canUseShell(roles, s));
}

export const SHELL_LABEL: Record<Shell, string> = {
  admin: 'Administration',
  mss: 'My team',
  ess: 'Self-service',
};

/**
 * Turns a server-supplied logical path into a route in the current shell.
 *
 * The server stores where a notification should go as `/leave` or `/privacy` —
 * a logical destination, not a URL. It has no business knowing that this SPA
 * mounts the same page under three prefixes, and encoding that in the database
 * would mean a frontend routing change required a data migration.
 *
 * Two exceptions. A path that already names a shell is used as-is, which is how
 * the DPO's request queue reaches `/admin/privacy` from anywhere. And `/` means
 * "the shell's home page" rather than the application root.
 */
export function shellRoute(shell: Shell, logicalPath: string): string {
  const path = logicalPath.trim();
  if (path === '' || path === '/') return shellPath(shell);

  const withSlash = path.startsWith('/') ? path : `/${path}`;
  for (const s of ['admin', 'mss', 'ess'] as Shell[]) {
    if (withSlash === `/${s}` || withSlash.startsWith(`/${s}/`)) return withSlash;
  }
  return `${shellPath(shell)}${withSlash}`;
}
